import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { updatePoolInfoAPI } from "./ApiViewLaunchpadAction";
import { FairLaunchTheme } from "../../LaunchPad/CeateFairLaunch/FairLaunchTheme";

const UpdatePoolInfo = ({
  open,
  onClose,
  poolAddr,
  setUpdate,
  update,
  media,
}) => {
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      logo: media?.StepThree?.Logo || "",
      website: media?.StepThree?.Website || "",
      facebook: media?.StepThree?.Facebook_Page || "",
      twitter: media?.StepThree?.Twitter || "",
      github: media?.StepThree?.Github || "",
      instagram: media?.StepThree?.Instagram || "",
      discord: media?.StepThree?.Discord || "",
      reddit: media?.StepThree?.Reddit || "",
      youtube: media?.StepThree?.Youtube || "",
      description: media?.StepThree?.Pool_Description || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      logo: Yup.string()
        .url("Must be a valid URL")
        .required("Logo URL is required"),
      website: Yup.string()
        .url("Must be a valid URL")
        .required("Website is required"),
      facebook: Yup.string().url("Must be a valid URL"),
      twitter: Yup.string().url("Must be a valid URL"),
      github: Yup.string().url("Must be a valid URL"),
      instagram: Yup.string().url("Must be a valid URL"),
      discord: Yup.string().url("Must be a valid URL"),
      reddit: Yup.string().url("Must be a valid URL"),
      youtube: Yup.string().url("Must be a valid URL"),
      description: Yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        await updatePoolInfoAPI(poolAddr, values);
        toast.success("Pool information updated successfully ✅");
        setUpdate((prev) => !prev);
        onClose();
      } catch (error) {
        toast.error("Failed to update pool information ❌");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: (theme) => ({
          ...FairLaunchTheme.cardStyle(theme),
          maxHeight: "90vh",
          m: { xs: 2, sm: 3 },
        }),
      }}
    >
      <DialogTitle
        sx={(theme) => ({
          ...FairLaunchTheme.gradientText(theme),
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          pb: 2,
        })}
      >
        Update Pool Information
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 2, sm: 2.5 },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              Update your presale information. Logo and Website are required.
            </Typography>

            <TextField
              fullWidth
              label="Logo URL *"
              name="logo"
              value={formik.values.logo}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.logo && Boolean(formik.errors.logo)}
              helperText={formik.touched.logo && formik.errors.logo}
              variant="outlined"
              placeholder="https://example.com/logo.png"
              size="small"
            />

            <TextField
              fullWidth
              label="Website *"
              name="website"
              value={formik.values.website}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.website && Boolean(formik.errors.website)}
              helperText={formik.touched.website && formik.errors.website}
              variant="outlined"
              placeholder="https://yourproject.com"
              size="small"
            />

            <Typography
              variant="subtitle2"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mt: 1,
                fontSize: { xs: "0.875rem", sm: "1rem" },
              })}
            >
              Social Media Links (Optional)
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: { xs: 2, sm: 2.5 },
              }}
            >
              <TextField
                fullWidth
                label="Facebook"
                name="facebook"
                value={formik.values.facebook}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.facebook && Boolean(formik.errors.facebook)
                }
                helperText={formik.touched.facebook && formik.errors.facebook}
                variant="outlined"
                placeholder="https://facebook.com/yourproject"
                size="small"
              />

              <TextField
                fullWidth
                label="Twitter"
                name="twitter"
                value={formik.values.twitter}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.twitter && Boolean(formik.errors.twitter)}
                helperText={formik.touched.twitter && formik.errors.twitter}
                variant="outlined"
                placeholder="https://twitter.com/yourproject"
                size="small"
              />

              <TextField
                fullWidth
                label="GitHub"
                name="github"
                value={formik.values.github}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.github && Boolean(formik.errors.github)}
                helperText={formik.touched.github && formik.errors.github}
                variant="outlined"
                placeholder="https://github.com/yourproject"
                size="small"
              />

              <TextField
                fullWidth
                label="Instagram"
                name="instagram"
                value={formik.values.instagram}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.instagram && Boolean(formik.errors.instagram)
                }
                helperText={formik.touched.instagram && formik.errors.instagram}
                variant="outlined"
                placeholder="https://instagram.com/yourproject"
                size="small"
              />

              <TextField
                fullWidth
                label="Discord"
                name="discord"
                value={formik.values.discord}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.discord && Boolean(formik.errors.discord)}
                helperText={formik.touched.discord && formik.errors.discord}
                variant="outlined"
                placeholder="https://discord.gg/yourproject"
                size="small"
              />

              <TextField
                fullWidth
                label="Reddit"
                name="reddit"
                value={formik.values.reddit}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.reddit && Boolean(formik.errors.reddit)}
                helperText={formik.touched.reddit && formik.errors.reddit}
                variant="outlined"
                placeholder="https://reddit.com/r/yourproject"
                size="small"
              />

              <TextField
                fullWidth
                label="YouTube"
                name="youtube"
                value={formik.values.youtube}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.youtube && Boolean(formik.errors.youtube)}
                helperText={formik.touched.youtube && formik.errors.youtube}
                variant="outlined"
                placeholder="https://youtube.com/c/yourproject"
                size="small"
                sx={{ gridColumn: { xs: "1", sm: "span 2" } }}
              />
            </Box>

            <TextField
              fullWidth
              label="Project Description"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
              variant="outlined"
              multiline
              rows={4}
              placeholder="Describe your project..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 2 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              mr: 1,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={(theme) => ({
              ...FairLaunchTheme.neonButton(theme),
              fontSize: { xs: "0.875rem", sm: "1rem" },
              padding: { xs: "8px 16px", sm: "10px 24px" },
            })}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Update"
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdatePoolInfo;
